import * as React from 'react';
import { Table, Button, Typography, Space, Tooltip } from 'antd';
import { EyeOutlined, SwapOutlined } from '@ant-design/icons';
import { ScriptRevision } from '../../types';
import { ScriptManagementStore } from '../../stores';
import { Utils } from '../../../common/services/Utils';
import RevisionComparisonModal from './RevisionComparisonModal';

const { Title } = Typography;

type Props = {
    store: ScriptManagementStore;
    revisions: ScriptRevision[];
    onViewRevision: (revision: ScriptRevision) => void;
};

const ScriptRevisionHistory: React.FC<Props> = ({ store, revisions, onViewRevision }) => {
    const [displayCount, setDisplayCount] = React.useState(10);
    const [selectedRevisions, setSelectedRevisions] = React.useState<ScriptRevision[]>([]);
    const [compareModalVisible, setCompareModalVisible] = React.useState(false);
    const [comparingRevisions, setComparingRevisions] = React.useState<[ScriptRevision, ScriptRevision] | null>(null);

    const displayedRevisions = React.useMemo(() => {
        return revisions.slice(0, displayCount);
    }, [revisions, displayCount]);

    const hasMore = displayCount < revisions.length;

    const handleShowMore = () => {
        setDisplayCount(prev => prev + 10);
    };

    const handleRowSelection = (_: React.Key[], selectedRows: ScriptRevision[]) => {
        if (selectedRows.length <= 2) {
            setSelectedRevisions(selectedRows);
        }
    };

    const handleCompare = () => {
        if (selectedRevisions.length === 2) {
            const sorted = [...selectedRevisions].sort(
                (a, b) => new Date(a.createDate).getTime() - new Date(b.createDate).getTime()
            );
            setComparingRevisions(sorted as [ScriptRevision, ScriptRevision]);
            setCompareModalVisible(true);
        }
    };

    const handleCompareWithLatest = (revision: ScriptRevision) => {
        const latestRevision = revisions[0];
        if (latestRevision && revision.id !== latestRevision.id) {
            setComparingRevisions([revision, latestRevision]);
            setCompareModalVisible(true);
        }
    };

    const handleCloseCompare = () => {
        setCompareModalVisible(false);
        setComparingRevisions(null);
    };

    const rowSelection = {
        selectedRowKeys: selectedRevisions.map(r => r.id),
        onChange: handleRowSelection,
        hideSelectAll: true,
        getCheckboxProps: (record: ScriptRevision) => ({
            disabled: selectedRevisions.length >= 2 && !selectedRevisions.find(r => r.id === record.id)
        })
    };

    const [olderRevision, newerRevision] = comparingRevisions || [null, null];

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 300,
            ellipsis: true
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            ellipsis: true
        },
        {
            title: 'Created At',
            dataIndex: 'createDate',
            key: 'createDate',
            width: 200,
            render: (date: string) => Utils.formatDateStringShort(date)
        },
        {
            title: 'Created By',
            dataIndex: 'createdBy',
            key: 'createdBy',
            width: 200,
            render: (createdBy: string) => store.getUserNameById(createdBy)
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_: unknown, record: ScriptRevision) => {
                const isLatest = revisions[0]?.id === record.id;
                return (
                    <Space size="small">
                        <Tooltip title="View Revision">
                            <Button
                                type="link"
                                icon={<EyeOutlined />}
                                onClick={() => onViewRevision(record)}
                                data-id={`view-revision-${record.id}`}
                            />
                        </Tooltip>
                        {!isLatest && (
                            <Tooltip title="Compare with latest">
                                <Button
                                    type="link"
                                    icon={<SwapOutlined />}
                                    onClick={() => handleCompareWithLatest(record)}
                                    data-id={`compare-with-latest-${record.id}`}
                                />
                            </Tooltip>
                        )}
                    </Space>
                );
            }
        }
    ];

    return (
        <div className="script-revision-history">
            <div className="script-revision-history-header">
                <Title level={5}>Revision History</Title>
                <Button
                    type="primary"
                    icon={<SwapOutlined />}
                    onClick={handleCompare}
                    disabled={selectedRevisions.length !== 2}
                    className="script-revision-history-compare-button"
                >
                    Compare Selected Revisions
                </Button>
            </div>
            <Table
                dataSource={displayedRevisions}
                columns={columns}
                rowKey="id"
                loading={store.isLoading}
                pagination={false}
                size="small"
                rowSelection={rowSelection}
            />
            {hasMore && (
                <Space className="script-revision-load-more">
                    <Button onClick={handleShowMore}>Show More</Button>
                </Space>
            )}
            <RevisionComparisonModal
                store={store}
                visible={compareModalVisible}
                olderRevision={olderRevision}
                newerRevision={newerRevision}
                onClose={handleCloseCompare}
            />
        </div>
    );
};

export default ScriptRevisionHistory;
